<template>
  <div v-if="!loading">
    <summary-navigator
      :export="exportTotals"
      :report-name="reportName"
      @officechanged="officeChanged"
      @companychanged="companyChanged"
    />
    <el-row :gutter="40">
      <el-col :span="12">
        <h2 class="columnHeader">{{ $t('orderReport.dailyTotals') }}</h2>
        <report-table />
      </el-col>
      <el-col :span="12">
        <h2 class="columnHeader">{{ $t('orderReport.weeklyTotals') }}</h2>
        <summary-table />
      </el-col>
    </el-row>
  </div>
  <div v-else>
    <p>{{ $t('orderReport.loading') }}</p>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';
import { DEFAULT_COMPANY } from '@/constants';
import { groupOrdersByPath } from '@/helpers';
import ReportTable from '@/components/table/ReportTable';
import SummaryTable from '@/components/table/SummaryTable';
import SummaryNavigator from '@/components/orders/SummaryNavigator';

function getWeekday(date) {
  return moment(date)
    .format('dddd')
    .toLowerCase();
}

function mealsForEventAndDiet(orders, mealEvent, diet) {
  const filteredByEvent = orders.filter((item) => item.menuInfo.event === mealEvent.id);
  const filteredByDiet = filteredByEvent.filter((order) => {
    const dietName = order.mealInfo.dietInfo.name;
    return dietName === diet;
  });
  return filteredByDiet && filteredByDiet.length > 0 ? filteredByDiet.length : null;
}

function filteredOrdersByMealEvent(orders, mealEvent) {
  const filteredByEvent = orders.filter((item) => item.menuInfo.event === mealEvent.id);
  let result = null;

  if (filteredByEvent && filteredByEvent.length > 0) {
    result = filteredByEvent.length;
  }
  return result;
}

function mealsForEvent(orders, mealEvent) {
  if (mealEvent.length === 0 || (orders && Object.keys(orders).length === 0)) {
    return [];
  }

  return filteredOrdersByMealEvent(orders, mealEvent);
}

export default {
  name: 'OrdersTotals',
  components: {
    ReportTable,
    SummaryTable,
    SummaryNavigator,
  },
  data() {
    return {
      company: DEFAULT_COMPANY,
    };
  },
  computed: {
    ...mapGetters('user', { user: 'getUser' }),
    ...mapGetters('orders', { orders: 'getWeekOrders' }),
    ...mapState('diets', { diets: (state) => state.diets }),
    ...mapState('events', { mealEvents: (state) => state.mealEvents }),
    ...mapState('weeklyMenu', { selectedWeek: (state) => state.selectedWeek }),
    ...mapState('offices', { offices: (state) => state.offices }),
    ...mapState('global', { adminSelectedOffice: 'adminSelectedOffice' }),
    ...mapState('orders', { loading: 'loading' }),

    officeName() {
      if (Object.keys(this.offices).length === 0) {
        return '';
      }
      const office = this.offices.find((o) => o.id === this.adminSelectedOffice);

      return office.name;
    },
    reportName() {
      const report = 'totals';
      return `${this.officeName}-${report}-${this.selectedWeek}.csv`;
    },
    exportTotals() {
      if (
        this.mealEvents.length < 0 ||
        this.diets.length < 0 ||
        (this.orders && Object.keys(this.orders).length === 0)
      ) {
        return [];
      }
      const data = [];
      data.push(this.getOfficeHeader);
      data.push(this.getEventHeaders);

      Object.keys(this.getOrdersByDate).forEach((date) => {
        data.push(this.getDayHeader(date));

        const orders = this.getOrdersByDate[date];
        this.diets.forEach((diet) => {
          const dietDetail = this.mealEvents.map((event) =>
            mealsForEventAndDiet(orders, event, diet.name)
          );
          dietDetail.unshift(this.$t(`orderReport.${[diet.name.toLowerCase()]}`));
          data.push(dietDetail);
        });
        data.push(this.getDayTotals(orders));
      });

      data.push(this.getTotals);
      return data;
    },
    getOrdersByDate() {
      return groupOrdersByPath(this.orders, 'menuInfo.date');
    },
    getOfficeHeader() {
      const officeName = this.user.officeInfo.name;
      return [`Wizeline: ${officeName}`];
    },
    getEventHeaders() {
      const eventHeaders = this.mealEvents.map((event) =>
        this.$t(`orderReport.${event.name.toLowerCase()}`)
      );
      eventHeaders.unshift('');
      return eventHeaders;
    },
    getTotals() {
      const totals = this.mealEvents.map((event) => mealsForEvent(this.orders, event));
      totals.unshift('Total');
      return totals;
    },
  },
  methods: {
    getDayHeader(date) {
      const dayHeader = [];
      dayHeader.push(this.$t(`orderReport.${getWeekday(date)}`));
      dayHeader.push(date);
      return dayHeader;
    },
    getDayTotals(orders) {
      const totals = this.mealEvents.map((event) => mealsForEvent(orders, event));
      totals.unshift('Total');
      return totals;
    },
    officeChanged(office) {
      this.$emit('officechanged', office);
    },
    companyChanged(company) {
      this.company = company;
      this.$emit('companychanged', company);
    },
    filteredByEvent(mealEvent) {
      if (this.orders && Object.keys(this.orders).length === 0) {
        return [];
      }
      return this.orders.filter((item) => item.menuInfo.event === mealEvent.id);
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
.columnHeader {
  margin: 2em auto 1em;
}
#locations {
  font-weight: 500;
}
.buttonsContainer {
  .buttonWrapper {
    display: inline-block;
  }
  .button,
  .hint {
    display: block;
  }
  .hint {
    font-size: 0.5em;
    text-align: center;
    color: $order-button-hint;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/orders/OrdersTotals.vue